/**
 * The centre strip. UI_UX section 7.
 *
 * Breadcrumb pills on the left, the Stack as a horizontal chip list in the
 * middle, and on the right the primary action button whose label is the only
 * verb the player needs now. Below 900px this becomes the sticky bottom action
 * bar and the Stack chips drop out, because the Stack is also visible as a
 * modal prompt whenever it needs an answer.
 */

import type { Action, GameState } from '@/engine';
import { Button } from '@/ui/system';
import { breadcrumbs } from './interaction';
import type { PrimaryVerb } from './interaction';
import styles from './Battlefield.module.css';

export interface CenterStripProps {
  state: GameState;
  localPlayer: string;
  verb: PrimaryVerb;
  onPrimary: () => void;
  /** Cancel whatever declaration or targeting is in progress. */
  onCancel?: () => void;
  cancelLabel?: string;
  /**
   * Legal actions that are not the one obvious next verb, such as Redraw during
   * setup. The primary button names one thing to do; these are the others.
   */
  secondary?: { label: string; action: Action }[];
  onSecondary?: (action: Action) => void;
  botThinking: boolean;
  onStackChipClick?: (sourceInstanceId: string) => void;
}

export function CenterStrip({
  state,
  localPlayer,
  verb,
  onPrimary,
  onCancel,
  cancelLabel,
  secondary,
  onSecondary,
  botThinking,
  onStackChipClick,
}: CenterStripProps) {
  const crumbs = breadcrumbs(state, localPlayer);

  return (
    <div className={styles.centerStrip}>
      <div className={styles.crumbs}>
        {crumbs.map((crumb, i) => (
          <span
            key={crumb}
            className={[styles.crumb, i === 1 && state.activePlayer === localPlayer ? styles.crumbActive : '']
              .filter(Boolean)
              .join(' ')}
          >
            {crumb}
          </span>
        ))}
      </div>

      <div className={styles.stackStrip} aria-label="The Stack">
        {state.stack.length === 0 ? (
          botThinking ? (
            <span className={styles.thinking}>
              <span className={styles.thinkingDot} />
              Opponent thinking
            </span>
          ) : (
            <span className={styles.stackEmpty}>Stack empty</span>
          )
        ) : (
          state.stack
            .slice()
            .reverse()
            .map((item) => (
              <button
                key={item.id}
                type="button"
                className={[styles.stackChip, item.controller === localPlayer ? styles.stackChipMine : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => onStackChipClick?.(item.sourceInstanceId)}
                title={`${item.label}. Controlled by ${state.players[item.controller]?.name ?? item.controller}.`}
              >
                {item.label}
              </button>
            ))
        )}
      </div>

      <div className={styles.secondaryActions}>
        {(secondary ?? []).map((entry) => (
          <Button
            key={entry.label}
            variant="secondary"
            size="small"
            onClick={() => onSecondary?.(entry.action)}
            data-testid={`secondary-${entry.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            {entry.label}
          </Button>
        ))}
        {onCancel ? (
          <Button variant="ghost" size="small" onClick={onCancel}>
            {cancelLabel ?? 'Cancel'}
          </Button>
        ) : null}
      </div>

      <div className={styles.primaryZone}>
        <Button
          variant={verb.tone}
          size="medium"
          disabled={verb.disabled}
          onClick={onPrimary}
          data-testid="primary-action"
        >
          {verb.label}
        </Button>
        {verb.hint ? <span className={styles.primaryHint}>{verb.hint}</span> : null}
      </div>
    </div>
  );
}
